import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { UzurvRestConfiguration, UzurvRestService, UZURV_REST_CONFIGURATION } from '@uzurv/http';
import { Deserialize } from 'cerialize';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Trip } from '../../models';

@Injectable({
  providedIn: 'root'
})
export class TripService extends UzurvRestService {
  readonly resourceUrl = 'trips/';

  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    domSanitizer: DomSanitizer
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /**
   * @param pageNumber
   * @param pageSize
   * @param transitProgramIds
   * @param vehicleTypeIds
   * @permission ride_monitoring
   */
  listRideRequests(
    pageNumber: number,
    pageSize: number,
    transitProgramIds?: Array<number>,
    vehicleTypeIds?: Array<number>
  ): Observable<Array<Trip>> {
    return this.get<Array<Trip>>(`${this.resourceUrl}pending/`, {
      pageNumber: pageNumber,
      pageSize: pageSize,
      transitProgramIds: transitProgramIds,
      vehicleTypeIds: vehicleTypeIds
    }).pipe(map(response => Deserialize(response, Trip)));
  }

  /**
   * @param pageNumber
   * @param pageSize
   * @param reservationStatusIds
   * @param vehicleTypeIds
   * @param transitProgramIds
   * @permission ride_monitoring
   */
  listReservationsByStatusAndVehicleTypeAndTransitProgram(
    pageNumber: number,
    pageSize: number,
    reservationStatusIds?: Array<number>,
    vehicleTypeIds?: Array<number>,
    transitProgramIds?: Array<number>
  ): Observable<Array<Trip>> {
    return this.get<Array<Trip>>(this.resourceUrl, {
      pageNumber: pageNumber,
      pageSize: pageSize,
      reservationStatusIds: reservationStatusIds,
      vehicleTypeIds: vehicleTypeIds,
      transitProgramIds: transitProgramIds
    }).pipe(map(response => Deserialize(response, Trip)));
  }

  /**
   * @param pageNumber
   * @param pageSize
   * @param cancelled
   * @permission ride_monitoring
   */
  listCancelledReservations(pageNumber: number, pageSize: number, cancelled = true): Observable<Array<Trip>> {
    return this.get<Array<Trip>>(`${this.resourceUrl}reservations/`, {
      pageNumber: pageNumber,
      pageSize: pageSize,
      cancelled: cancelled
    }).pipe(map(response => Deserialize(response, Trip)));
  }

  /**
   * @param pageNumber
   * @param pageSize
   * @param driverId
   * @param inactive
   * @permission reservation_read
   */
  listReservationsByDriverId(pageNumber: number, pageSize: number, driverId: number, inactive?: boolean): Observable<Array<Trip>> {
    return this.get<Array<Trip>>(this.resourceUrl, {
      pageNumber: pageNumber,
      pageSize: pageSize,
      driverId: driverId,
      inactive: inactive
    }).pipe(map(response => Deserialize(response, Trip)));
  }

  /**
   * @param pageNumber
   * @param pageSize
   * @param nonaccepted
   * @permission nonaccepted_ride_read
   */
  listNonacceptedRideRequests(pageNumber: number, pageSize: number, nonaccepted = true): Observable<Array<Trip>> {
    return this.get<Array<Trip>>(this.resourceUrl, { pageNumber: pageNumber, pageSize: pageSize, nonaccepted: nonaccepted }).pipe(
      map(response => Deserialize(response, Trip))
    );
  }

  /**
   * @param searchString
   * @param pageNumber
   * @param pageSize
   * @param unactivatedWillCall
   * @permission trip_search
   */
  searchTrips(searchString: string, pageNumber: number, pageSize: number, nonactivatedWillCall?: boolean): Observable<Array<Trip>> {
    return this.get<Array<Trip>>(`search/${this.resourceUrl}`, {
      searchString: searchString,
      pageNumber: pageNumber,
      pageSize: pageSize,
      nonactivatedWillCall: nonactivatedWillCall
    }).pipe(map(response => Deserialize(response, Trip)));
  }
}
